import { ChevronLeft, ChevronRight, Facebook, Globe, Instagram, Linkedin, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefusalTag } from "@/components/Badges";
import { SOURCE_LABELS } from "@/lib/constants";

const LEGAL_CLS = { actif: "text-emerald-700 bg-emerald-50", "radié": "text-[#FF3B30] bg-red-50", "en liquidation": "text-amber-700 bg-amber-50" };

const Socials = ({ lead }) => (
  <div className="flex gap-1.5">
    {[["linkedin", Linkedin], ["facebook", Facebook], ["instagram", Instagram]].map(([k, Icon]) =>
      lead[k] ? (
        <a key={k} href={lead[k]} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-zinc-500 transition-colors hover:text-[#002FA7]">
          <Icon className="h-4 w-4" />
        </a>
      ) : null,
    )}
  </div>
);

const Row = ({ lead, checked, toggle, onOpen }) => (
  <tr data-testid={`lead-row-${lead.id}`} onClick={() => onOpen(lead.id)} className={`cursor-pointer transition-colors hover:bg-zinc-50 ${lead.do_not_call ? "bg-red-50/40" : ""}`}>
    <td onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`lead-select-${lead.id}`} checked={checked} onCheckedChange={toggle} /></td>
    <td className="max-w-[280px]">
      <div className="truncate font-semibold text-zinc-950" title={lead.name}>{lead.name}</div>
      <div className="truncate text-xs text-muted-foreground">{lead.category || "—"}</div>
      {lead.do_not_call && <div className="mt-1.5"><RefusalTag testId={`refusal-tag-${lead.id}`} /></div>}
    </td>
    <td><div>{lead.city || "—"}</div><div className="text-xs text-muted-foreground">{[lead.npa, lead.address].filter(Boolean).join(" · ")}</div></td>
    <td className="font-mono text-xs"><div>{lead.phone || "—"}</div>{lead.mobile && <div className="text-muted-foreground">{lead.mobile}</div>}</td>
    <td className="max-w-[220px] truncate text-xs">{lead.email ? <span className={lead.email_valid ? "" : "text-amber-700"}>{lead.email}</span> : "—"}</td>
    <td className="max-w-[180px] truncate text-xs">
      {lead.website ? <a href={lead.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"><Globe className="h-3 w-3" />{lead.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</a> : "—"}
    </td>
    <td className="text-xs">{lead.dirigeant || "—"}</td>
    <td><Socials lead={lead} /></td>
    <td><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${LEGAL_CLS[lead.legal_status] || "bg-zinc-100 text-zinc-500"}`}>{lead.legal_status || "inconnu"}</span></td>
    <td><div className="flex gap-1">{(lead.sources || []).map((s) => <span key={s} className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium">{SOURCE_LABELS[s] || s}</span>)}</div></td>
  </tr>
);

const Pagination = ({ data, page, setPage, pageSize, setPageSize, loading }) => (
  <div className="flex flex-col items-start justify-between gap-3 border-t border-border px-4 py-3 text-sm sm:flex-row sm:items-center">
    <div data-testid="leads-total" className="flex items-center gap-2 text-muted-foreground">
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      <span className="font-mono font-semibold text-zinc-950">{data?.total?.toLocaleString("fr-CH") ?? 0}</span> leads · page {data?.page ?? 1} / {data?.pages ?? 1}
    </div>
    <div className="flex items-center gap-2">
      <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
        <SelectTrigger data-testid="page-size-select" className="h-8 w-28"><SelectValue /></SelectTrigger>
        <SelectContent>{[25, 50, 100, 250].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}</SelectContent>
      </Select>
      <Button size="icon" variant="outline" className="h-8 w-8" data-testid="page-prev-button" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
      <Button size="icon" variant="outline" className="h-8 w-8" data-testid="page-next-button" disabled={page >= (data?.pages ?? 1)} onClick={() => setPage(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
    </div>
  </div>
);

export const LeadsTable = ({ data, loading, page, setPage, pageSize, setPageSize, selected, setSelected, onOpen }) => {
  const items = data?.items || [];
  const allChecked = items.length > 0 && items.every((l) => selected.includes(l.id));
  const toggleAll = () => setSelected(allChecked ? [] : items.map((l) => l.id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
      <div className="max-h-[68vh] overflow-auto">
        <table data-testid="leads-table" className="data-table w-full whitespace-nowrap text-sm">
          <thead>
            <tr>
              <th className="w-10"><Checkbox data-testid="leads-select-all" checked={allChecked} onCheckedChange={toggleAll} /></th>
              <th>Raison sociale</th><th>Ville</th><th>Téléphone / Mobile</th><th>E-mail</th><th>Site web</th><th>Dirigeant</th><th>Réseaux</th><th>Statut RC</th><th>Sources</th>
            </tr>
          </thead>
          <tbody>
            {items.map((lead) => <Row key={lead.id} lead={lead} checked={selected.includes(lead.id)} toggle={() => toggle(lead.id)} onOpen={onOpen} />)}
            {!items.length && (
              <tr><td colSpan={10} data-testid="leads-empty" className="py-16 text-center text-muted-foreground">{loading ? "Chargement…" : "Aucun lead ne correspond à ces filtres."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination data={data} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} loading={loading} />
    </div>
  );
};
